import React, { useState } from 'react';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import { 
  Share2, Link2, Copy, Check, Lock,
  Clock, X, Mail
} from 'lucide-react';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

export default function ShareDialog({ 
  isOpen, 
  onClose, 
  resourceType, // 'event', 'photo', 'album', 'story', 'timeline'
  resourceId, 
  familyId,
  resourceTitle 
}) {
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [settings, setSettings] = useState({
    permission: resourceType === 'event' ? 'rsvp' : 'view_only',
    expires_in_days: 30,
    password: '',
    usePassword: false
  });

  const createShareLink = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/share`, {
        resource_type: resourceType,
        resource_id: resourceId,
        family_id: familyId,
        permission: settings.permission,
        expires_in_days: settings.expires_in_days,
        password: settings.usePassword ? settings.password : null
      });
      
      // Use the frontend URL
      const frontendUrl = window.location.origin;
      setShareUrl(`${frontendUrl}/shared/${res.data.share_token}`);
    } catch (err) {
      alert('Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      alert('Failed to copy');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl max-w-md w-full shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Share2 className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Share {resourceType}</h2>
              <p className="text-gray-400 text-sm">{resourceTitle}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5">
          {!shareUrl ? (
            <>
              {/* Permission */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Permission</label>
                <select
                  value={settings.permission}
                  onChange={(e) => setSettings({ ...settings, permission: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="view_only">View Only</option>
                  {resourceType === 'event' && <option value="rsvp">Allow RSVP</option>}
                  {(resourceType === 'photo' || resourceType === 'album') && (
                    <option value="download">Allow Download</option>
                  )}
                </select>
              </div>

              {/* Expiration */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Link Expires In
                </label>
                <select
                  value={settings.expires_in_days}
                  onChange={(e) => setSettings({ ...settings, expires_in_days: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={365}>1 year</option>
                </select>
              </div>

              {/* Password Protection */}
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer mb-2">
                  <input
                    type="checkbox"
                    checked={settings.usePassword}
                    onChange={(e) => setSettings({ ...settings, usePassword: e.target.checked })}
                    className="rounded border-slate-500 bg-slate-700 text-blue-500 focus:ring-blue-500"
                  />
                  <Lock className="w-4 h-4" />
                  Password protect this link
                </label>
                {settings.usePassword && (
                  <input
                    type="text"
                    value={settings.password}
                    onChange={(e) => setSettings({ ...settings, password: e.target.value })}
                    placeholder="Enter password"
                    className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  />
                )}
              </div>

              <button
                onClick={createShareLink}
                disabled={loading || (settings.usePassword && !settings.password)}
                className="w-full py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Link2 className="w-5 h-5" />
                    Create Share Link
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              {/* Share URL Created */}
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-2 text-green-400 mb-2">
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Link Created!</span>
                </div>
                <p className="text-gray-400 text-sm">
                  Anyone with this link can {settings.permission === 'rsvp' ? 'RSVP to' : 'view'} this {resourceType}.
                </p>
              </div>

              {/* Copy URL */}
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="flex-1 px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                    copied 
                      ? 'bg-green-600 text-white' 
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>

              {/* Share via other methods */}
              <div className="flex gap-2">
                <a
                  href={`mailto:?subject=Check out this ${resourceType}&body=${encodeURIComponent(shareUrl)}`}
                  className="flex-1 py-2 bg-slate-700 text-gray-300 rounded-lg hover:bg-slate-600 flex items-center justify-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Check out this ${resourceType}: ${shareUrl}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  WhatsApp
                </a>
              </div>

              <button
                onClick={() => {
                  setShareUrl('');
                  onClose();
                }}
                className="w-full mt-4 py-2 text-gray-400 hover:text-white"
              >
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
